import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Check, Settings2 } from "lucide-react";

const STORAGE_KEY = "dds_cookie_consent_v1";
const REOPEN_EVENT = "dds:open-cookie-banner";

// Functional cookies are always required (auth, session, PWA). Analytics are optional.
const DEFAULT_PREFS = { functional: true, analytics: false, accepted: false };

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : DEFAULT_PREFS;
  } catch { return DEFAULT_PREFS; }
}

export function openCookieBanner() {
  window.dispatchEvent(new Event(REOPEN_EVENT));
}

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) setOpen(true);
    const handler = () => {
      const existing = getConsent();
      setPrefs(existing.accepted ? existing : DEFAULT_PREFS);
      setShowCustomize(true);
      setOpen(true);
    };
    window.addEventListener(REOPEN_EVENT, handler);
    return () => window.removeEventListener(REOPEN_EVENT, handler);
  }, []);

  const save = (next) => {
    const final = { ...next, accepted: true, decided_at: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(final));
    setPrefs(final);
    setOpen(false);
  };

  const acceptAll = () => save({ functional: true, analytics: true });
  const rejectOptional = () => save({ functional: true, analytics: false });

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Consentement aux cookies"
      data-testid="cookie-banner"
      className="fixed bottom-4 inset-x-4 sm:bottom-6 sm:left-6 sm:right-auto sm:max-w-md z-[60] bg-white text-[#1a2332] rounded-2xl shadow-2xl border border-black/10 overflow-hidden font-body"
    >
      <div className="p-5 md:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FF8C42]/10 flex items-center justify-center shrink-0">
            <Cookie className="w-5 h-5 text-[#FF8C42]" />
          </div>
          <div className="flex-1">
            <h3 className="font-headings font-black text-lg text-[#1a2332]">Cookies & vie privée</h3>
            <p className="mt-2 text-sm text-[#3a4858] leading-relaxed">
              Nous utilisons des cookies <strong>techniques</strong> nécessaires (authentification, PWA) et,
              avec votre accord, des cookies de mesure d'audience pour améliorer le site.{" "}
              <Link to="/confidentialite" className="text-[#00a86b] underline hover:no-underline" data-testid="cookie-privacy-link">
                En savoir plus
              </Link>
            </p>
          </div>
          <button
            onClick={rejectOptional}
            aria-label="Refuser les cookies optionnels"
            data-testid="cookie-close"
            className="p-1 rounded-lg text-[#9aa5bc] hover:bg-black/5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {showCustomize && (
          <div className="mt-4 space-y-3 bg-[#f8faff] border border-black/5 rounded-xl p-4" data-testid="cookie-prefs">
            <PrefRow
              label="Cookies fonctionnels"
              desc="Authentification, session, PWA. Nécessaires au fonctionnement."
              checked
              disabled
              testid="cookie-functional"
            />
            <PrefRow
              label="Mesure d'audience"
              desc="Statistiques anonymisées de visite. Optionnel."
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              testid="cookie-analytics"
            />
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            onClick={acceptAll}
            data-testid="cookie-accept-all"
            className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1.5 bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-xl px-4 py-2.5 text-sm transition-colors"
          >
            <Check className="w-4 h-4" /> Tout accepter
          </button>
          <button
            onClick={rejectOptional}
            data-testid="cookie-reject"
            className="flex-1 min-w-[120px] inline-flex items-center justify-center bg-white border-2 border-[#1a2332]/15 hover:border-[#1a2332]/30 text-[#1a2332] font-headings font-bold rounded-xl px-4 py-2.5 text-sm transition-colors"
          >
            Refuser
          </button>
          {!showCustomize ? (
            <button
              onClick={() => setShowCustomize(true)}
              data-testid="cookie-customize"
              className="inline-flex items-center justify-center gap-1.5 text-[#00a86b] hover:text-[#007a4e] font-headings font-bold text-sm px-2"
            >
              <Settings2 className="w-3.5 h-3.5" /> Personnaliser
            </button>
          ) : (
            <button
              onClick={() => save(prefs)}
              data-testid="cookie-save-prefs"
              className="inline-flex items-center justify-center gap-1.5 bg-[#00a86b] hover:bg-[#007a4e] text-white font-headings font-bold rounded-xl px-4 py-2.5 text-sm transition-colors"
            >
              Enregistrer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PrefRow({ label, desc, checked, disabled, onChange, testid }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? "opacity-70" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        data-testid={testid}
        className="mt-1 w-4 h-4 accent-[#00a86b]"
      />
      <span className="flex-1">
        <span className="block font-headings font-bold text-sm text-[#1a2332]">{label}</span>
        <span className="block text-xs text-[#5b6577] mt-0.5">{desc}</span>
      </span>
    </label>
  );
}
